import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageShell from './PageShell';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import { publicApi } from '../api';
import { useApi } from '../hooks/useApi';
import { staticImg, fmtMoney } from '../utils/media';
import { Spinner, EmptyState } from '../components/StateHelpers';
import { ArrowRight, Tag, Search as SearchIcon, PackageOpen } from 'lucide-react';

const badgeText = (p) => (p.is_discounted ? 'İNDİRİM' : p.is_new ? 'YENİ' : p.is_featured ? 'POPÜLER' : null);
const badgeStyle = (b) => (b === 'YENİ' ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : b === 'POPÜLER' ? 'bg-blue-500/15 border-blue-500/40 text-blue-300' : 'bg-[#e4b44e]/15 border-[#e4b44e]/40 text-[#f4d896]');

export default function Products() {
  const { lang, tr } = useI18n();
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '';
  const [q, setQ] = useState('');

  const { data: cats } = useApi(() => publicApi.categories(lang), [lang]);
  const { data: prods, loading } = useApi(() => publicApi.products(lang, category || undefined), [lang, category]);

  useEffect(() => { document.title = 'Aegis Studios — Ürünler'; }, []);

  const list = useMemo(() => {
    return (prods || []).filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  }, [prods, q]);

  return (
    <PageShell eyebrow={tr('shop.eyebrow', 'MAĞAZA')} title={<><span className="text-white">{tr('shop.title1', 'Efsanevi')}</span> <span className="gold-text">{tr('shop.title2', 'Ürünler')}</span></>} subtitle={tr('shop.subtitle', 'Metin2 sunucunuza sıfırdan zirveye eşlik eden premium files, sistemler ve modüller.')}>
      <Seo title={tr('shop.title2', 'Ürünler')} />
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-8 md:mb-10">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setParams({})} className={`h-10 px-4 rounded-full border text-xs font-bold tracking-widest transition ${!category ? 'btn-gold' : 'border-white/12 text-white/70 hover:text-white hover:border-[#e4b44e]/40'}`}>{tr('shop.all', 'TÜMÜ')}</button>
              {(cats || []).map((c) => (
                <button key={c.id} onClick={() => setParams({ category: c.slug })} className={`h-10 px-4 rounded-full border text-xs font-bold tracking-widest transition ${category === c.slug ? 'btn-gold' : 'border-white/12 text-white/70 hover:text-white hover:border-[#e4b44e]/40'}`}>{c.name.toUpperCase()}</button>
              ))}
            </div>
            <div className="md:ml-auto relative">
              <SearchIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr('shop.search_ph', 'Ürün ara…')} className="w-full md:w-72 h-11 pl-11 pr-4 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#e4b44e]/60 transition" />
            </div>
          </div>

          {loading ? <Spinner /> : list.length === 0 ? (
            <EmptyState icon={PackageOpen} title={tr('shop.empty_title', 'Ürün Bulunamadı')} subtitle={tr('shop.empty_sub', 'Bu kategoride henüz ürün yok.')} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {list.map((p) => {
                const badge = badgeText(p);
                return (
                  <article key={p.id} className="group relative rounded-2xl overflow-hidden border border-white/8 bg-gradient-to-b from-[#111015] to-[#0a090c] card-hover">
                    <Link to={`/products/${p.slug}`} className="block">
                      <div className="relative aspect-[4/3] overflow-hidden bg-[#0f0e12]">
                        <img src={staticImg(p.image)} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                        {badge && <div className={`absolute top-4 left-4 px-2.5 py-1 text-[10px] font-black tracking-widest border rounded ${badgeStyle(badge)}`}>{badge}</div>}
                        <div className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/50 backdrop-blur border border-white/15 flex items-center justify-center text-white/70"><Tag className="w-3.5 h-3.5" /></div>
                      </div>
                      <div className="p-5">
                        <div className="text-[10px] font-bold tracking-[0.3em] text-[#e4b44e]/80 mb-2">{(p.category_name || '').toUpperCase()}</div>
                        <h3 className="font-display text-lg font-bold text-white leading-snug min-h-[3.2rem]">{p.name}</h3>
                        <div className="mt-4 flex items-end justify-between">
                          <div>
                            {p.old_price ? <div className="text-xs text-white/40 line-through">{fmtMoney(p.old_price)} ₺</div> : null}
                            <div className="font-display text-2xl font-black gold-text">{fmtMoney(p.price)} ₺</div>
                          </div>
                          <span className="w-10 h-10 rounded-full btn-gold flex items-center justify-center"><ArrowRight className="w-4 h-4" /></span>
                        </div>
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
